import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export const useTopicBookmarks = (topicId?: string) => {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();

  // Only fetch the FULL bookmarks list (heavy join) when no topicId is given
  // — that's the Bookmarks page. On a topic page, do a tiny lookup instead.
  const fetchFullList = !topicId;

  const { data: bookmarks, isLoading } = useQuery({
    queryKey: ['topic-bookmarks', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('bookmarks')
        .select(`
          *,
          topics (
            id,
            title,
            slug,
            last_post_at,
            reply_count,
            view_count,
            category_id,
            categories (name, color, slug)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && !loading && fetchFullList,
  });

  // Lightweight "is this one topic bookmarked" lookup for topic pages.
  const { data: singleBookmarked } = useQuery({
    queryKey: ['topic-bookmark-one', user?.id, topicId],
    queryFn: async () => {
      if (!user?.id || !topicId) return false;
      const { data, error } = await supabase
        .from('bookmarks')
        .select('id')
        .eq('user_id', user.id)
        .eq('topic_id', topicId)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!user?.id && !loading && !!topicId,
    staleTime: 30_000,
  });

  const isBookmarked = fetchFullList
    ? Boolean(topicId && bookmarks?.some((b: any) => b.topic_id === topicId))
    : !!singleBookmarked;

  // Get bookmark count for a topic
  const { data: bookmarkCount } = useQuery({
    queryKey: ['topic-bookmark-count', topicId],
    queryFn: async () => {
      if (!topicId) return 0;
      
      const { count, error } = await supabase
        .from('bookmarks')
        .select('*', { count: 'exact', head: true })
        .eq('topic_id', topicId);
        
      if (error) throw error;
      return count || 0;
    },
    enabled: !!topicId,
  });

  // Toggle bookmark mutation
  const toggleBookmark = useMutation({
    mutationFn: async (topicId: string) => {
      if (!user?.id) throw new Error('Must be logged in');

      // Check if bookmark exists
      const { data: existingBookmark } = await supabase
        .from('bookmarks')
        .select('id')
        .eq('user_id', user.id)
        .eq('topic_id', topicId)
        .maybeSingle();

      if (existingBookmark) {
        // Delete bookmark
        const { error } = await supabase
          .from('bookmarks')
          .delete()
          .eq('user_id', user.id)
          .eq('topic_id', topicId);
          
        if (error) throw error;
        return { action: 'removed' };
      } else {
        // Create bookmark
        const { error } = await supabase
          .from('bookmarks')
          .insert({
            user_id: user.id,
            topic_id: topicId
          });
          
        if (error) {
          if (error.code === '23505') {
            return { action: 'exists' };
          }
          throw error;
        }
        return { action: 'added' };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['topic-bookmarks', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['topic-bookmark-one', user?.id, topicId] });
      queryClient.invalidateQueries({ queryKey: ['topic-bookmark-count', topicId] });
      
      if (result.action === 'added') {
        toast.success('Topic bookmarked!');
      } else if (result.action === 'removed') {
        toast.success('Topic removed from bookmarks');
      }
    },
    onError: () => {
      toast.error('Failed to update bookmark');
    },
  });

  return {
    bookmarks,
    isLoading,
    isBookmarked,
    bookmarkCount,
    toggleBookmark: (topicId: string) => toggleBookmark.mutate(topicId),
    isToggling: toggleBookmark.isPending,
  };
};
